import React from 'react';
import { Typography, Card, Row, Col, theme } from 'antd';
import { useNavigate } from 'react-router-dom';
import { CalendarOutlined, LoginOutlined, MailOutlined, UserAddOutlined, UserOutlined } from '@ant-design/icons';

import styled from 'styled-components';
import { useClient } from './common/ClientProvider';
import { useMember } from './common/member/MemberContext';
import { useClientConfig } from './common/client/ClientConfigProvider';
import { AdminPermissions, BookingPermissions, MemberPermissions } from './model/auth/Permissions';
import { tokens } from './core/theme/tokens';

const DEFAULT_BACKGROUND_URL =
  'https://images.unsplash.com/photo-1540497077202-7c8a3999166f?auto=format&fit=crop&q=80&w=2070';

// Reject any background URL that isn't a clean http(s) URL — guards against
// CSS injection via quotes / parens leaking through the styled-component
// template literal.
const safeBackgroundUrl = (url: string): string =>
  /^https?:\/\/[^'")\s]+$/.test(url) ? url : DEFAULT_BACKGROUND_URL;

// Parse a #RRGGBB string into an `r, g, b` triple for rgba() interpolation.
// Returns the Southbank navy as a fallback for non-hex inputs.
const hexToRgbTriple = (hex: string): string => {
  const match = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!match) return '0, 0, 90';
  const value = parseInt(match[1], 16);
  return `${(value >> 16) & 0xff}, ${(value >> 8) & 0xff}, ${value & 0xff}`;
};

const { Title } = Typography;

// PoC for FORT-179: spacing / type literals that map 1:1 onto the design tokens
// are sourced from `tokens` instead of being hardcoded. Bespoke values with no
// token equivalent (the 24px/20px radii, the rgba brand gradients) are left for
// the branding-through-tokens work in FORT-180.
const PageContainer = styled.div<{ $backgroundUrl: string; $primaryRgb: string }>`
  min-height: calc(100vh - 64px);
  padding: ${tokens.space[7]}px ${tokens.space[5]}px;
  background: linear-gradient(135deg, rgba(${props => props.$primaryRgb}, 0.05) 0%, rgba(${props => props.$primaryRgb}, 0.15) 100%), url('${props => props.$backgroundUrl}') no-repeat center center;
  background-size: cover;
  background-attachment: fixed;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  border-radius: 24px;
  padding: ${tokens.space[7]}px;
  box-shadow: 0 12px 32px rgba(0, 0, 90, 0.1);
`;

const StyledCard = styled(Card)<{ $large?: boolean }>`
  height: ${props => (props.$large ? 280 : 200)}px;
  margin: ${tokens.space[4]}px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: relative;
  overflow: hidden;
  border-radius: 20px;
  border: none;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);

  .card-content {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    transition: transform 0.3s ease;
    padding: ${tokens.space[5]}px;
  }

  .card-description {
    position: absolute;
    bottom: -100%;
    left: 0;
    right: 0;
    background: rgba(0, 0, 90, 0.95);
    color: white;
    padding: 20px;
    transition: bottom 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    text-align: center;
    font-size: ${tokens.fontSize.md}px;
    font-weight: ${tokens.fontWeight.medium};
  }

  &:hover {
    box-shadow: 0 12px 28px rgba(0, 0, 90, 0.15);
    transform: translateY(-5px);
    
    .card-content {
      transform: translateY(-15px);
    }
    
    .card-description {
      bottom: 0;
    }
  }
`;

const IconWrapper = styled.div<{ $large?: boolean; $color: string }>`
  font-size: ${props => (props.$large ? 72 : 48)}px;
  margin-bottom: 20px;
  color: ${props => props.$color};
  transition: transform 0.3s ease;

  ${StyledCard}:hover & {
    transform: scale(1.1);
  }
`;

interface TileProps {
  title: string;
  icon: React.ReactNode;
  description: string;
  onClick: () => void;
  large?: boolean;
  color: string;
}

const Tile: React.FC<TileProps> = ({ title, icon, description, onClick, large, color }) => (
  <StyledCard onClick={onClick} $large={large}>
    <div className="card-content">
      <IconWrapper $large={large} $color={color}>{icon}</IconWrapper>
      <Title level={large ? 3 : 4} style={{ margin: 0, color, textAlign: 'center' }}>{title}</Title>
    </div>
    <div className="card-description">
      {description}
    </div>
  </StyledCard>
);

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { currentClientId } = useClient();
  const { permissions, isLoading: permissionsLoading } = useMember();
  const { branding, contact } = useClientConfig();
  const { token } = theme.useToken();
  const primaryColor = token.colorPrimary;
  const backgroundUrl = safeBackgroundUrl(branding.backgroundImageUrl || branding.heroImageUrl || DEFAULT_BACKGROUND_URL);
  const primaryRgb = hexToRgbTriple(primaryColor);
  const contactEmail = contact.email;

  const hasPermission = (name: string) =>
    permissions?.permissions?.some((p) => p.name === name);

  const isAdminOrFoh =
    !permissionsLoading &&
    (hasPermission(AdminPermissions.ClubAdmin) ||
     hasPermission(AdminPermissions.FortiusAdmin) ||
     hasPermission(AdminPermissions.FrontOfHouse));

  // Guest Signup: admin/FoH OR members:createguest
  const showGuestSignup =
    !permissionsLoading &&
    (isAdminOrFoh || hasPermission(MemberPermissions.CanSignUpGuest));

  // New Member: admin/FoH OR user has no membershipId (needs to sign up)
  const showNewMember =
    !permissionsLoading &&
    (isAdminOrFoh || !permissions?.member?.membershipId);

  // Bookings: user has any booking permission
  const showBookings =
    !permissionsLoading &&
    (isAdminOrFoh ||
     hasPermission(BookingPermissions.Read) ||
     hasPermission(BookingPermissions.Create) ||
     hasPermission(BookingPermissions.Edit) ||
     hasPermission(BookingPermissions.Cancel));

  // My Profile: user has a membershipId
  const showMyProfile =
    !permissionsLoading && !!permissions?.member?.membershipId;

  // RFC 5322 is huge; we only need to reject obvious header-injection vectors
  // (?, &, whitespace, control chars) and anything that doesn't look like
  // local@domain. A tenant admin who saves contact.email as
  // `victim@x.com?bcc=attacker@y.com&body=...` would otherwise have every
  // member's "Contact" tap silently pre-fill the mail client with hidden
  // recipients.
  const isSafeEmailForMailto = (email: string): boolean =>
    /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(email);

  const handleContact = () => {
    const email = contactEmail.trim();
    if (!email || !isSafeEmailForMailto(email)) return;
    window.location.href = `mailto:${encodeURIComponent(email)}`;
  };

  const tiles: React.ReactNode[] = [];

  if (showGuestSignup) {
    tiles.push(
      <Col key="guest" xs={24} sm={24} md={12} lg={12} xl={10}>
        <Tile
          title="Visitor / Guest Sign-In"
          icon={<LoginOutlined />}
          description="Tap here to sign in a visitor or guest"
          onClick={() => navigate('/guest-signup')}
          large
          color={primaryColor}
        />
      </Col>
    );
  }

  if (showNewMember) {
    tiles.push(
      <Col key="newmember" xs={24} sm={24} md={12} lg={12} xl={10}>
        <Tile
          title="New Member Sign-Up"
          icon={<UserAddOutlined />}
          description="Tap here to register a new member"
          onClick={() => navigate('/newmember')}
          large
          color={primaryColor}
        />
      </Col>
    );
  }

  if (showBookings) {
    tiles.push(
      <Col key="bookings" xs={24} sm={12} md={12} lg={12} xl={10}>
        <Tile
          title="Bookings"
          icon={<CalendarOutlined />}
          description={`Book a facility at ${currentClientId}`}
          onClick={() => navigate('/bookings/facilities')}
          color={primaryColor}
        />
      </Col>
    );
  }

  if (showMyProfile) {
    tiles.push(
      <Col key="myprofile" xs={24} sm={12} md={12} lg={12} xl={10}>
        <Tile
          title="My Profile"
          icon={<UserOutlined />}
          description="View and manage your profile"
          onClick={() => navigate('/my-profile')}
          color={primaryColor}
        />
      </Col>
    );
  }

  // Always show Contact as a fallback tile
  tiles.push(
    <Col key="contact" xs={24} sm={12} md={12} lg={12} xl={10}>
      <Tile
        title="Contact"
        icon={<MailOutlined />}
        description={`Contact ${currentClientId}`}
        onClick={handleContact}
        color={primaryColor}
      />
    </Col>
  );

  return (
    <PageContainer $backgroundUrl={backgroundUrl} $primaryRgb={primaryRgb}>
      <ContentWrapper>
        <Title style={{
          textAlign: 'center',
          marginBottom: '56px',
          color: primaryColor,
          fontSize: '42px',
          fontWeight: tokens.fontWeight.bold
        }}>
          Welcome to {currentClientId}
        </Title>

        <Row gutter={[24, 24]} justify="center">
          {tiles}
        </Row>
      </ContentWrapper>
    </PageContainer>
  );
};

export default HomePage;